/**
 * Runtime stack of currently-open dialogs.
 *
 * Answers the *visual* question — "is this dialog painted on top of another
 * one?" — which drives nested sizing (a confirm over a dialog should read as a
 * subordinate prompt, not a same-size replacement) and scrim suppression (two
 * 50% overlays stack to an effective ~75% and turn the page muddy).
 *
 * This deliberately does NOT use React context. Dialogs are frequently
 * rendered as *siblings* of the dialog they appear over rather than inside its
 * children — see `QueueDetailDialog`, where `<ConfirmDialog>` sits next to
 * `<StyledDialog>` in a fragment. Tree position therefore does not reflect
 * visual stacking, but this counter does.
 *
 * Feedback ownership asks a different question — "which dialog does the
 * component that raised this belong to?" — and *that* one is genuinely
 * tree-shaped. It uses React context instead; see `DialogLayerContext`.
 */

let openCount = 0;

const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of listeners) listener();
}

/** `useSyncExternalStore` subscribe: called whenever the open-dialog count changes. */
export function subscribeOpenDialogs(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** True while at least one dialog is open (`useSyncExternalStore` snapshot). */
export function hasOpenDialog(): boolean {
  return openCount > 0;
}

export interface DialogSlot {
  /** 0 for the bottom-most open dialog, 1 for one stacked on it, and so on. */
  depth: number;
  /** Release the slot. Idempotent — safe to call from an effect cleanup twice. */
  release: () => void;
}

/**
 * Claim a slot for a dialog that is becoming visible.
 *
 * Call from a layout effect, not from render: the returned depth feeds a
 * setState, and doing that before paint is what keeps a nested dialog from
 * flashing at full width for one frame before it shrinks.
 */
export function pushOpenDialog(): DialogSlot {
  const depth = openCount;
  openCount += 1;
  notify();

  let released = false;
  return {
    depth,
    release: () => {
      // StrictMode runs effect → cleanup → effect, so release can be invoked
      // more than once for the same slot.
      if (released) return;
      released = true;
      // Clamp at zero. A release that outlives its reset — a test harness
      // clearing the stack before React unmounts the tree, say — would
      // otherwise drive the count negative, and every dialog opened afterwards
      // would compute a negative depth, read as top-level, and silently stop
      // shrinking or suppressing its scrim. A count of open dialogs has no
      // meaningful value below zero, so refuse to represent one.
      openCount = Math.max(0, openCount - 1);
      notify();
    },
  };
}

/** Currently-open dialog count. Exposed for assertions; not used in render. */
export function openDialogCount(): number {
  return openCount;
}

/** Test hygiene: drop any slots leaked by a component that never unmounted. */
export function resetDialogStack(): void {
  openCount = 0;
  notify();
}
